import { useEffect } from 'react';

/**
 * Checks if an event is coming from a ReactModalPortal (i.e. Our modals).
 * Https://stackoverflow.com/a/39245638/11479152.
 *
 * @param {Event} event The event.
 * @returns {boolean}
 */
const isEventFromPortal = event => {
  const path = event.path || (event.composedPath && event.composedPath());
  return path.some(p => p.className === 'ReactModalPortal');
};

/**
 * Detects when a click has been performed outside a component.
 * Taken from: Https://usehooks.com/useOnClickOutside/.
 *
 * @param {Object} ref The component's ref.
 * @param {Function} handler The function to call on outside click.
 * @param {boolean} [ignoreModals] Whether to ignore clicks from modals as well.
 */
const useClickOutside = (ref, handler, ignoreModals) => {
  useEffect(
    () => {
      const listener = event => {
        // Do nothing if clicking ref's element or descendent elements
        if (
          !ref.current ||
          ref.current.contains(event.target) ||
          (ignoreModals && isEventFromPortal(event))
        ) {
          return;
        }

        handler(event);
      };

      document.addEventListener('click', listener);

      return () => {
        document.removeEventListener('click', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new
    // function on every render that will cause this effect
    // callback/cleanup to run every render. It's not a big deal
    // but to optimize you can wrap handler in useCallback before
    // passing it into this hook.
    [ref, handler],
  );
};

export default useClickOutside;
